import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid2'
import Button from '@mui/material/Button'
import defaultTexts from '../../../texts/texts'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'
import ServiceItem from './ServiceItem'

const SubscriptionItem = (props) => {
  const {
    id,
    title,
    base_price,
    currency,
    duration,
    services,
    package_name,
    clientSubscriptions,
    setCancelId,
    setUpdateProps,
    handleCancelSubscriptionDialogOpen,
    handleUpdateSubscriptionDialogOpen,
    updateRenewableStatus,
  } = props
  const theme = useTheme()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [backendServices, setBackendServices] = useState([])
  const [frontendServices, setFrontendServices] = useState([])
  const [optionalServices, setOptionalServices] = useState([])
  const [isCurrent, setIsCurrent] = useState(false)  
  const [totalPrice, setTotalPrice] = useState(base_price)
  const [currentOptionalServices, setCurrentOptionalServices] = useState([])
  const [selectedOptionalServices, setSelectedOptionalServices] = useState([])
  const [isUpdate, setIsUpdate] = useState(false)
  const [renewable, setRenewable] = useState(true)
  const [currentRenewable, setCurrentRenewable] = useState(false)

  const wrapperStyle = {
    background: theme.palette.oxfordBlue,
    marginBottom: '20px',
    padding: '20px',
    color: 'white',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  }

  const headStyle = {
    width: '100%',
    borderBottom: 'white 1px solid',
    marginBottom: '20px',
    position: 'relative',
  }

  const labelStyle = {
    fontSize: '18px',
    fontWeight: '500',
  }

  const priceStyle = {
    color: theme.palette.violetsAreBlue,
    fontSize: '24px',
    fontWeight: '500',
    textAlign: 'right',
  }

  const serviceLabelStyle = {
    color: theme.palette.greenSheen,
    fontSize: '18px',
    fontWeight: '500',
    marginBottom: '20px',
  }

  const currentLabelStyle = {
    height: '60px',
    background: theme.palette.greenSheen,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    left: '-20px',
    top: '-20px',
  }

  const subscribeButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const cancelButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.coralRed,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.coralRed, 0.8),
    },
  }

  const smallSaveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '16px',
    height: '30px',
    borderRadius: '20px',
    padding: '0px 20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const renewableCheckboxStyle = {
    color: 'white',

    '&.Mui-checked': {
      color: 'white',
    },

    '& .MuiSvgIcon-root': {
      fontSize: 28,
    },
  }

  const renewableCheckboxLabelStyle = {
    color: 'white',
    fontSize: '17px',
  }

  const handleChangeRenewable = (value) => {
    setRenewable(value)
  }

  const handleClickSubscribe = () => {
    setUpdateProps({
      id,
      totalPrice,
      renewable,
      selectedOptionalServices,
    })

    handleUpdateSubscriptionDialogOpen(totalPrice)
  }

  const handleClickCancel = () => {
    const package_object = clientSubscriptions.find(item => item.package_name === package_name)
    const current_id = package_object ? package_object.id : null
    setCancelId(current_id)
    handleCancelSubscriptionDialogOpen()
  }

  useEffect(() => {
    setBackendServices(services.filter(item => item.service_type === 'backend'))
    setFrontendServices(services.filter(item => item.service_type === 'frontend'))
    setOptionalServices(services.filter(item => item.service_type !== 'backend' && item.service_type !== 'frontend'))
  }, [services])
  
  useEffect(() => {
    const package_object = clientSubscriptions.find(item => item.package_name === package_name)
    setIsCurrent(package_object ? true : false)
  }, [package_name, clientSubscriptions])

  useEffect(() => {
    if (isCurrent) {
      let current_optional_services = []

      const package_object = clientSubscriptions.find(item => item.package_name === package_name)

      if (package_object && package_object.services) {        
        package_object.services.forEach(item => {
          if (!item.is_mandatory) {
            current_optional_services.push(item.service)
          }
        })     
      }

      setCurrentRenewable(package_object ? package_object.renewable : false)
      setCurrentOptionalServices(current_optional_services)
      setSelectedOptionalServices(current_optional_services)
    } else {
      setCurrentRenewable(false)
      setCurrentOptionalServices([])
      setSelectedOptionalServices([])
    }
  }, [isCurrent, clientSubscriptions, package_name, services])

  useEffect(() => {
    let total_price = parseFloat(base_price)

    services.forEach(item => {
      if (!item.is_mandatory && selectedOptionalServices.includes(item.service)) {
        total_price += parseFloat(item.price)
      }
    })

    setTotalPrice(total_price)
  }, [base_price, selectedOptionalServices, services])
  
  useEffect(() => {
    setIsUpdate(isCurrent && JSON.stringify(currentOptionalServices) !== JSON.stringify(selectedOptionalServices))
  }, [isCurrent, currentOptionalServices, selectedOptionalServices, renewable, currentRenewable])

  useEffect(() => {
    setRenewable((isCurrent && !currentRenewable) ? false : true)
  }, [isCurrent, currentRenewable])
  
  return (
    <>
      <Box
        sx={{
          ...wrapperStyle,
          borderRadius: isCurrent ? '50px 20px 20px 20px' : '20px',
        }}
      >

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems='flex-start'
          justifyContent='space-between'
          sx={headStyle}
        >
          {
            isCurrent &&
            <Box sx={currentLabelStyle}>
              <Box>{defaultTexts.current}</Box>
            </Box>              
          }
          <Box
            sx={{
              ...labelStyle,
              paddingLeft: isCurrent ? '200px' : '0px',
            }}
          >{title.value}</Box>
          <Box>
            <Box sx={priceStyle}>{totalPrice} {currency.toUpperCase()} / {capitalizeFirstLetter(duration)}</Box>
            {
              editPermission ?
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='flex-end'
                spacing={1}
              >
                {
                  isCurrent && renewable !== currentRenewable &&
                  <Button
                    variant='contained'
                    disableRipple
                    disableElevation
                    disabled={false}
                    onClick={() => updateRenewableStatus(renewable)}
                    sx={smallSaveButtonStyle}
                  >
                    {defaultTexts.save}
                  </Button>
                }
                <CustomCheckboxField
                  label={defaultTexts.renewable}
                  checked={renewable}
                  handleChange={handleChangeRenewable}
                  sx={renewableCheckboxStyle}
                  sxLabel={renewableCheckboxLabelStyle}
                />
              </Stack> :
              <Box sx={{ paddingTop: '10px' }}></Box>
            }                      
          </Box>          
        </Stack>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={serviceLabelStyle}>{defaultTexts.openStackServices}</Box>
            {
              backendServices.map((item, index) => (
                <ServiceItem
                  key={index}
                  {...item}
                  selectedOptionalServices={selectedOptionalServices}
                  setSelectedOptionalServices={setSelectedOptionalServices}
                />
              ))
            }
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={serviceLabelStyle}>{defaultTexts.userInterfaces}</Box>
            {
              frontendServices.map((item, index) => (
                <ServiceItem
                  key={index}
                  {...item}
                  selectedOptionalServices={selectedOptionalServices}
                  setSelectedOptionalServices={setSelectedOptionalServices}
                />
              ))
            }
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={serviceLabelStyle}>{defaultTexts.optionalServices}</Box>
            {
              optionalServices.map((item, index) => (
                <ServiceItem
                  key={index}
                  {...item}
                  selectedOptionalServices={selectedOptionalServices}
                  setSelectedOptionalServices={setSelectedOptionalServices}
                />
              ))
            }
          </Grid>
        </Grid>
        
        {
          editPermission &&
          <Stack
            direction='row'
            justifyContent='flex-end'
            spacing={2}
          >
            {
              isCurrent ?
              <>
                {
                  isUpdate &&
                  <Button
                    variant='contained'
                    disableRipple
                    disableElevation
                    disabled={false}
                    onClick={handleClickSubscribe}
                    sx={subscribeButtonStyle}
                  >
                    {defaultTexts.update}
                  </Button>
                }
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickCancel}
                  sx={cancelButtonStyle}
                >
                  {defaultTexts.cancel}
                </Button>
              </> :
              <Button
                variant='contained'
                disableRipple
                disableElevation
                disabled={false}
                onClick={handleClickSubscribe}
                sx={subscribeButtonStyle}
              >
                {defaultTexts.subscribe}
              </Button>
            }            
          </Stack>
        }        

      </Box>
    </>
  )
}

export default SubscriptionItem